import { createContext, useContext } from "react";
import {
  activityOccurrenceStatusSchema,
  groupStatusSchema,
  isGroup,
  visitStatusSchema,
  type INonRoutableEmployeePerformedActivityOccurrenceOrGroup,
} from "@medoma/models/activities";
import type { IVisit, IVisitStatus } from "@medoma/models/visits";
import { isDraggedItemRoutable } from "./dragAndDropUtils";
import type { IUnplannedOccurrenceOrGroup } from "@medoma/models/activities";

// Where a drag starts decides what is being dragged: the sidebar holds unplanned
// occurrences and groups, a shift lane holds the occurrences that are not route
// work, and a route holds visits. Pairing the two in one union means a consumer
// reads the item it knows it has instead of asking which of the three it got.
export type IDrag =
  | {
      source: "sidebar";
      item: IUnplannedOccurrenceOrGroup;
    }
  | {
      source: "shift-lane";
      shiftId: number;
      item: INonRoutableEmployeePerformedActivityOccurrenceOrGroup;
    }
  | {
      source: "route";
      shiftId: number;
      routeId: string;
      item: IVisit;
    };

export type IDraggedItem = IDrag["item"];

type AvailableDropZones = {
  sidebar: boolean;
  route: boolean;
  shift: boolean;
};

const noDropZones: AvailableDropZones = {
  sidebar: false,
  route: false,
  shift: false,
};

// A visit that is under way or over is not re-planned: it does not move between
// the sidebar, a shift and a route. Both sources are read without cancelled, so
// the switch answers for the statuses that reach the board and no others, and
// it is exhaustive so a new visit status cannot compile until someone decides
// which side of that it falls on.
const visitStatusIsReplannable = (
  visitStatus: IVisitStatus | null | undefined,
): boolean => {
  switch (visitStatus) {
    case null:
    case undefined:
    case visitStatusSchema.enum.planned:
    case visitStatusSchema.enum.travellingTo:
      return true;
    case visitStatusSchema.enum.ongoing:
    case visitStatusSchema.enum.finished:
      return false;
  }
};

// A group and a visit are the same thing under two field names, and an occurrence
// answers with a status of its own, so the reading is per source.
const isDraggedItemReplannable = (drag: IDrag): boolean => {
  switch (drag.source) {
    case "sidebar":
      return isGroup(drag.item)
        ? visitStatusIsReplannable(drag.item.visitStatus)
        : drag.item.status !== activityOccurrenceStatusSchema.enum.finished;
    case "shift-lane":
      return isGroup(drag.item)
        ? drag.item.visitStatus !== groupStatusSchema.enum.finished &&
            drag.item.visitStatus !== groupStatusSchema.enum.cancelled
        : drag.item.status !== activityOccurrenceStatusSchema.enum.finished;
    case "route":
      return visitStatusIsReplannable(drag.item.status);
  }
};

function computeAvailableDropZones(
  drag: IDrag | undefined,
): AvailableDropZones {
  if (!drag || !isDraggedItemReplannable(drag)) {
    return noDropZones;
  }

  return {
    // What came from the sidebar is unplanned already, so it has nothing to be
    // unplanned back to.
    sidebar: drag.source !== "sidebar",
    route: isDraggedItemRoutable(drag),
    shift: true,
  };
}

type DragAndDropState = {
  drag: IDrag | undefined;
  availableDropZones: AvailableDropZones;
};

type DragAndDropAction =
  | {
      type: "START_DRAG";
      drag: IDrag;
    }
  | { type: "END_DRAG" };

export function dragAndDropReducer(
  state: DragAndDropState,
  action: DragAndDropAction,
): DragAndDropState {
  switch (action.type) {
    case "START_DRAG":
      return {
        drag: action.drag,
        availableDropZones: computeAvailableDropZones(action.drag),
      };
    case "END_DRAG":
      return {
        drag: undefined,
        availableDropZones: noDropZones,
      };
    default:
      return state;
  }
}

type DragAndDropContextType = {
  drag: IDrag | undefined;
  availableDropZones: AvailableDropZones;
  startDrag: (drag: IDrag) => void;
  endDrag: () => void;
};

export const DragAndDropContext = createContext<DragAndDropContextType>({
  drag: undefined,
  availableDropZones: noDropZones,
  startDrag: () => {},
  endDrag: () => {},
});

export const useDragAndDropContext = () => useContext(DragAndDropContext);
